import type { TenantFixture } from './types'

/**
 * DEMO DATA ONLY. 676 Competitions is the design partner, and this file is the
 * one place in the web app that knows the name: no screen, generator or rule
 * may branch on it. Figures are invented and seeded, not a copy of anything
 * real.
 */
export const demo676: TenantFixture = {
  id: 'demo_676',
  name: '676 Competitions',
  seed: 676,
  currency: 'GBP',
  locale: 'en-GB',
  accentColor: '#2ee6d6',
  user: { name: 'Alex Morgan', email: 'alex.morgan@example.com', role: 'owner' },
  websites: [
    { id: 'web_676_prod', name: '676competitions.co.uk', environment: 'production', trafficShare: 1 },
    {
      id: 'web_676_staging',
      name: 'staging.676competitions.co.uk',
      environment: 'staging',
      trafficShare: 0.012,
    },
  ],
  // Its tracking started late, so a default 30-day range reaches back before the baseline.
  trackingBaselineDaysAgo: 9,
  traffic: {
    dailyVisitors: 2600,
    weekdayFactors: [1.18, 0.9, 0.92, 0.95, 1.02, 1.08, 1.14],
    dailyGrowth: 0.004,
  },
  funnelRates: {
    competition_viewed: 0.74,
    ticket_selector_used: 0.6,
    add_to_basket: 0.64,
    checkout_started: 0.72,
    payment_attempted: 0.88,
    purchase_completed: 0.95,
  },
  commerce: {
    ticketsPerOrder: 4.6,
    customersPerOrder: 0.86,
    newCustomerShare: 0.34,
    attributedShare: 0.58,
    unlistedDemandWeight: 2.5,
  },
  devices: [
    { id: 'mobile', label: 'Mobile', share: 0.66 },
    { id: 'desktop', label: 'Desktop', share: 0.27 },
    { id: 'tablet', label: 'Tablet', share: 0.07 },
  ],
  customerTypes: [
    { id: 'new', label: 'New', share: 0.34 },
    { id: 'returning', label: 'Returning', share: 0.66 },
  ],
  competitions: [
    { id: 'cmp_iphone17', name: 'iPhone 17 Pro Max', category: 'Tech', ticketPrice: 0.99, maxEntries: 9500, opensInDays: -18, durationDays: 28, weight: 1.6 },
    { id: 'cmp_rolex', name: 'Rolex Datejust 41', category: 'Watches', ticketPrice: 2.49, maxEntries: 8000, opensInDays: -20, durationDays: 30, weight: 1.1 },
    { id: 'cmp_mini', name: 'Mini Cooper S', category: 'Cars', ticketPrice: 1.99, maxEntries: 9000, opensInDays: -26, durationDays: 35, weight: 1 },
    { id: 'cmp_cash10k', name: '£10,000 Tax-Free Cash', category: 'Cash', ticketPrice: 0.79, maxEntries: 6000, opensInDays: -9, durationDays: 21, weight: 1.4 },
    { id: 'cmp_ps5', name: 'PlayStation 5 Pro Bundle', category: 'Tech', ticketPrice: 0.49, maxEntries: 2600, opensInDays: -12, durationDays: 14, weight: 0.9 },
    { id: 'cmp_ducati', name: 'Ducati Monster SP', category: 'Bikes', ticketPrice: 2.99, maxEntries: 3500, opensInDays: -6, durationDays: 30, weight: 0.45 },
    { id: 'cmp_kitchen', name: 'Dream Kitchen Makeover', category: 'Home', ticketPrice: 1.49, maxEntries: 3500, opensInDays: -15, durationDays: 20, weight: 0.35 },
    { id: 'cmp_dubai', name: 'Dubai Getaway for Two', category: 'Travel', ticketPrice: 1.29, maxEntries: 1500, opensInDays: -3, durationDays: 10, weight: 0.8 },
    { id: 'cmp_instant500', name: 'Instant Win £500 Bundle', category: 'Instant wins', ticketPrice: 0.25, maxEntries: 8000, opensInDays: -30, durationDays: 45, weight: 0.7 },
    { id: 'cmp_tesla', name: 'Tesla Model 3', category: 'Cars', ticketPrice: 3.99, maxEntries: 12500, opensInDays: -40, durationDays: 38, weight: 1 },
    { id: 'cmp_macbook', name: 'MacBook Pro 16"', category: 'Tech', ticketPrice: 0.89, maxEntries: 9000, opensInDays: -75, durationDays: 30, weight: 1.2 },
    { id: 'cmp_rangerover', name: 'Range Rover Sport', category: 'Cars', ticketPrice: 4.49, maxEntries: 20000, opensInDays: -80, durationDays: 42, weight: 1.1 },
    { id: 'cmp_cash5k', name: '£5,000 Cash Drop', category: 'Cash', ticketPrice: 0.59, maxEntries: 7000, opensInDays: -55, durationDays: 21, weight: 1.2 },
    { id: 'cmp_golfr', name: 'VW Golf R', category: 'Cars', ticketPrice: 2.49, maxEntries: 14000, opensInDays: -62, durationDays: 28, weight: 1 },
    { id: 'cmp_airpods', name: 'AirPods Pro Bundle', category: 'Tech', ticketPrice: 0.39, maxEntries: 5000, opensInDays: -45, durationDays: 12, weight: 0.7 },
    { id: 'cmp_cash25k', name: '£25,000 Cash', category: 'Cash', ticketPrice: 1.99, maxEntries: 16000, opensInDays: -58, durationDays: 30, weight: 1.3 },
    { id: 'cmp_gamingpc', name: 'Ultimate Gaming PC', category: 'Tech', ticketPrice: 0.99, maxEntries: 4200, opensInDays: -48, durationDays: 25, weight: 0.8 },
  ],
  selfReportedAnswers: [
    { answer: 'Facebook ad', weight: 0.3 },
    { answer: 'Instagram', weight: 0.18 },
    { answer: 'YouTube - DM Performance', weight: 0.1 },
    { answer: 'A friend', weight: 0.09 },
    { answer: 'YouTube - 676 Competitions', weight: 0.06 },
    { answer: 'TikTok', weight: 0.04 },
    { answer: 'Google', weight: 0.03 },
    { answer: 'Watched a live draw', weight: 0.012 },
    { answer: 'Other', weight: 0.03 },
    { answer: null, weight: 0.158 },
  ],
  channels: [
    { id: 'meta_ads', name: 'Meta Ads', kind: 'digital', dailySpend: 180, attributionWeight: 0.34, trafficShare: 0.22 },
    { id: 'google_ads', name: 'Google Ads', kind: 'digital', dailySpend: 70, attributionWeight: 0.12, trafficShare: 0.08 },
    { id: 'tiktok_ads', name: 'TikTok Ads', kind: 'digital', dailySpend: 55, attributionWeight: 0.06, trafficShare: 0.06 },
    { id: 'email', name: 'Email', kind: 'digital', dailySpend: 8, attributionWeight: 0.16, trafficShare: 0.07 },
    { id: 'push', name: 'Push notifications', kind: 'digital', dailySpend: 0, attributionWeight: 0.07, trafficShare: 0.04 },
    { id: 'organic_social', name: 'Organic social', kind: 'digital', dailySpend: 0, attributionWeight: 0.1, trafficShare: 0.12 },
    { id: 'referral', name: 'Referral partners', kind: 'digital', dailySpend: 15, attributionWeight: 0.04, trafficShare: 0.03 },
    { id: 'billboard', name: 'Billboards', kind: 'offline', dailySpend: 60, attributionWeight: 0.03, trafficShare: 0.01 },
    { id: 'flyer', name: 'Flyers', kind: 'offline', dailySpend: 12, attributionWeight: 0.02, trafficShare: 0.01 },
    { id: 'venue_screen', name: 'Venue screens', kind: 'offline', dailySpend: 25, attributionWeight: 0.04, trafficShare: 0.015 },
    { id: 'event', name: 'Event stands', kind: 'offline', dailySpend: 30, attributionWeight: 0.02, trafficShare: 0.005 },
  ],
  campaigns: [
    { id: 'camp_supercar', name: 'Supercar September', channels: ['meta_ads', 'tiktok_ads', 'billboard'] },
    { id: 'camp_iphone', name: 'iPhone 17 launch', channels: ['meta_ads', 'google_ads', 'email', 'push'] },
    { id: 'camp_cash', name: 'Autumn cash giveaway', channels: ['meta_ads', 'organic_social', 'flyer'] },
    { id: 'camp_matchday', name: 'Matchday venue takeover', channels: ['venue_screen', 'event', 'flyer'] },
  ],
  creatives: [
    { id: 'cr_supercar_reel', name: 'Supercar reel 15s', campaignId: 'camp_supercar', channel: 'meta_ads' },
    { id: 'cr_supercar_a6', name: 'A6 roadside billboard', campaignId: 'camp_supercar', channel: 'billboard' },
    { id: 'cr_iphone_carousel', name: 'iPhone carousel', campaignId: 'camp_iphone', channel: 'meta_ads' },
    { id: 'cr_iphone_search', name: 'iPhone search ad', campaignId: 'camp_iphone', channel: 'google_ads' },
    { id: 'cr_cash_flyer', name: 'Cash giveaway A5 flyer', campaignId: 'camp_cash', channel: 'flyer' },
    { id: 'cr_matchday_loop', name: 'Matchday screen loop', campaignId: 'camp_matchday', channel: 'venue_screen' },
  ],
  automations: [
    { id: 'auto_basket', name: 'Abandoned basket reminder', status: 'running', trigger: 'Basket abandoned for 1 hour' },
    { id: 'auto_80sold', name: '80% sold push', status: 'running', trigger: 'Competition reaches 80% sold' },
    { id: 'auto_pace', name: 'Behind-pace boost', status: 'running', trigger: 'Competition behind expected pace' },
    { id: 'auto_close', name: 'Last-chance email', status: 'running', trigger: '24 hours to close' },
    { id: 'auto_payment', name: 'Payment failure alert', status: 'running', trigger: 'Payment failure rate above 8%' },
    { id: 'auto_weekly', name: 'Weekly performance report', status: 'running', trigger: 'Every Monday at 08:00' },
    { id: 'auto_winback', name: 'Lapsed customer win-back', status: 'paused', trigger: 'No purchase in 60 days' },
    { id: 'auto_market', name: 'Competitor prize watch', status: 'draft', trigger: 'Competitor launches a similar prize' },
  ],
  anomalies: { recentDays: 2, checkoutConversion: 0.86, paymentFailureRate: 0.11 },
}
